import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { getProviders, languageLabel, providersLabel, providersOf } from "../../../lib/libraryAdmin";
import { Chip, ErrorNote, InfoRow } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetMessage,
  SheetRow,
  SheetRows,
  SheetSection,
} from "../../../components/settings-sheet";
import { DatabaseIcon } from "../../../components/icons";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/metadata")({
  component: MetadataSources,
});

/**
 * Where this server's metadata comes from. Read-only: the agent is a
 * property of the deployment, and each library ranks the sources on the
 * library itself (Plex puts it in the same place) — the first searches and
 * owns the identity, the rest fill in what it lacks.
 */
function MetadataSources() {
  const { serverId } = Route.useParams();
  // See the Library page: the registry hydrates after first render.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;

  const providers = useQuery({
    queryKey: ["settings", "providers", serverId],
    staleTime: 5 * 60_000,
    enabled: isServer,
    queryFn: () => getProviders((source as ServerSource).conn),
  });

  const libraries = useQuery({
    queryKey: ["settings", "libraries", serverId],
    staleTime: 30_000,
    enabled: isServer,
    queryFn: () => source!.getLibraries(),
  });

  return (
    <ServerSettingsGuard serverId={serverId} heading="Metadata sources">
      {(server) => (
        <div>
          <SheetHeader title="Metadata sources" />

          <>
              {providers.error ? (
                <SheetInset>
                  <ErrorNote error={providers.error} />
                </SheetInset>
              ) : null}

              <SheetSection title="Agent">
                <InfoRow
                  label="Agent"
                  hint="Titles, artwork, summaries and episode data come from the Rago metadata service, which proxies the sources below."
                >
                  Rago Metadata
                </InfoRow>
              </SheetSection>

              <SheetSection title="Sources" flush>
                {providers.isLoading ? (
                  <SheetMessage loading>Loading…</SheetMessage>
                ) : (providers.data ?? []).length === 0 ? (
                  <SheetMessage>
                    No metadata service is configured on this server, so scans leave items
                    unmatched.
                  </SheetMessage>
                ) : (
                  <SheetRows>
                    {(providers.data ?? []).map((p) => {
                      const users = (libraries.data ?? []).filter((l) => providersOf(l).includes(p.id));
                      return (
                        <SheetRow key={p.id} align="start">
                          <DatabaseIcon className="mt-0.5 shrink-0 text-[var(--rago-text-2)]" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="text-sm text-[var(--rago-text-1)]">{p.title}</span>
                              {p.default ? <Chip tone="ok">Default</Chip> : null}
                            </span>
                            <span className="mt-0.5 block truncate font-mono text-xs text-[var(--rago-text-3)]">
                              {p.baseUrl}
                            </span>
                            <span className="mt-1 block text-xs text-[var(--rago-text-3)]">
                              {users.length > 0
                                ? `Used by: ${users
                                    .map((l) =>
                                      providersOf(l)[0] === p.id ? l.title : `${l.title} (fills gaps)`,
                                    )
                                    .join(", ")}`
                                : "Not used by any library"}
                            </span>
                          </span>
                        </SheetRow>
                      );
                    })}
                  </SheetRows>
                )}
              </SheetSection>

              {(libraries.data ?? []).length > 0 ? (
                <SheetSection title="Libraries" flush>
                  <SheetRows>
                    {(libraries.data ?? []).map((l) => (
                      <SheetRow key={l.id} className="text-sm">
                        <span className="min-w-0 truncate text-[var(--rago-text-1)]">{l.title}</span>
                        <span className="shrink-0 text-xs text-[var(--rago-text-3)]">
                          {providersLabel(providersOf(l))} · {languageLabel(l.language)}
                        </span>
                      </SheetRow>
                    ))}
                  </SheetRows>
                </SheetSection>
              ) : null}
          </>
        </div>
      )}
    </ServerSettingsGuard>
  );
}
